/**
 * BookNow 追蹤碼注入與事件派送
 *
 * 追蹤碼由 booknow_api 的 /brand、/store、/get 回應中的 tracking 節點提供：
 *   { ga: [...], gtm: [...], pixel: [...], line: [...] }
 * 對應 brand_trace 表 loc = 'book' 的設定（type 1 = GA/GA4、2 = GTM、3 = Meta Pixel、4 = LINE Tag）。
 *
 * 因為 BookNow V2 是純靜態 SPA（沒有 PHP 渲染 index.html），追蹤碼只能等 API 回來才知道，
 * 所以這裡的 init 都設計成可重複呼叫、內部自行去重。
 *
 * Ocard 自己只掛 GA4，不掛 Pixel；LINE Tag 也只支援品牌自己的
 * （Ocard 那組的 sharedCookieDomain 是 ocard.co，在 booknow.tw 網域下不會生效）。
 *
 * 品牌有設 GTM 時，每個事件也會同步推進 dataLayer（event 名稱以 booknow_ 開頭），
 * 品牌自己在 GTM 建觸發條件使用。用自訂名稱而不是 purchase 這類 GA4 標準名稱：
 * 品牌的 GTM 常與官網共用，標準名稱會直接觸發他們既有的電商代碼，把 0 元預約算成訂單。
 */

// Ocard 自己的 GA4，永遠掛
export const OCARD_GA = 'G-36LELG0V9W';

// 事件對照表：同一個動作在 GA4、Meta Pixel、品牌 GTM 的事件名稱不同
// GTM 不沿用 GA4 名稱：GA4 的 view_item / view_item_list 各代表兩種動作，品牌在 GTM 會分不出來
export const EVENTS = {
  VIEW_BRAND: { ga: 'view_item_list', pixel: null, gtm: 'booknow_view_brand' },
  VIEW_STORE: { ga: 'view_item', pixel: 'ViewContent', gtm: 'booknow_view_store' },
  // 分店頁的服務清單曝光。有了它，select_item 才有分母可以算點擊率
  // （GA4 的商品清單瀏覽次數 / 點擊次數 / 點閱率三個指標）
  VIEW_SERVICE_LIST: { ga: 'view_item_list', pixel: null, gtm: 'booknow_view_service_list' },
  // 點開服務詳情。Meta 不另外送：ViewContent 已用在分店頁，重複用會混淆，
  // 而 Meta 的漏斗 PageView -> ViewContent -> InitiateCheckout -> Schedule 已經完整
  SELECT_SERVICE: { ga: 'select_item', pixel: null, gtm: 'booknow_select_service' },
  // 點開服務詳情等同於查看商品詳情，因此同時送 view_item，
  // 這樣服務才有瀏覽數，可以算出服務層級的「瀏覽 -> 預約」轉換率
  VIEW_SERVICE: { ga: 'view_item', pixel: null, gtm: 'booknow_view_service' },
  BEGIN_BOOK: { ga: 'begin_checkout', pixel: 'InitiateCheckout', gtm: 'booknow_begin_booking' },
  // 用 purchase 而非 generate_lead：GA4 只會處理特定一組電子商務事件的 items
  // （view_item_list、select_item、view_item、begin_checkout、purchase 等），
  // generate_lead 不在其中，items 不會進商品報表 —— 偏偏這是漏斗最重要的一步。
  // 代價是營利報表會出現 0 元交易，但 BookNow 本來就不看營收。
  // 送出時必須帶 value 與 currency，否則 GA4 不會正確處理 purchase。
  // Meta 維持預約專用的標準事件 Schedule。
  BOOK_SUCCESS: { ga: 'purchase', pixel: 'Schedule', gtm: 'booknow_booking_success' },
  BOOK_CONFIRM: { ga: 'booking_confirm', pixel: null, gtm: 'booknow_booking_confirm' },
  BOOK_CANCEL: { ga: 'booking_cancel', pixel: null, gtm: 'booknow_booking_cancel' },
  // 加入行事曆。method：apple / google（手機直接開）、list（電腦跳清單）、
  // other（手機點「使用其他行事曆」）、in_app_browser（在 LINE 等內建瀏覽器被擋下）、
  // failed（清單載入失敗，改給直接連結）
  ADD_TO_CALENDAR: { ga: 'add_to_calendar', pixel: null, gtm: 'booknow_add_to_calendar' },
};

// GA4 的電商欄位，推給 GTM 時另外放進 ecommerce，品牌的 GA4 代碼勾「傳送電子商務資料」就讀得到
const ECOMMERCE_KEYS = ['transaction_id', 'value', 'currency', 'items', 'item_list_id', 'item_list_name'];

// 項目類型，寫進 item_category2，用來區分同一份報表裡的分店與服務
export const ITEM_TYPE = {
  STORE: '分店',
  SERVICE: '服務',
};

/**
 * 組出追蹤用的 item_id：`品牌slug/名稱/token`
 * 三個組成都是公開值（brandId 在網址上、token 是 encode_hash 的雜湊），不含內部 idx。
 * 名稱本身可能含 '/'（production 有 125 個店名、15 個服務名如此），會破壞格式，先換成 '-'。
 */
export function itemId(brandId, name, token) {
  const safeName = String(name || '').replace(/\//g, '-').trim();

  return `${brandId}/${safeName}/${token}`;
}

// 已初始化的追蹤碼，避免重複注入
const loaded = {
  ga: [],
  gtm: [],
  pixel: [],
  line: [],
};

const base = {
  ga: false,
  pixel: false,
  line: false,
};

function injectScript(src) {
  const el = document.createElement('script');
  el.async = true;
  el.src = src;
  document.head.appendChild(el);
}

// 過濾出還沒初始化過的追蹤碼
function fresh(kind, codes) {
  const result = [];
  (codes || []).forEach((raw) => {
    const code = String(raw || '').trim();
    if (code && loaded[kind].indexOf(code) === -1 && result.indexOf(code) === -1) {
      result.push(code);
    }
  });

  return result;
}

// ---------------------------------------------------------------- GA4 / gtag

function ensureGaBase(firstId) {
  if (base.ga) return;
  base.ga = true;

  window.dataLayer = window.dataLayer || [];
  // 必須沿用官方 snippet 的 arguments 寫法，改成 rest 參數送出的資料結構會不對
  // eslint-disable-next-line func-names, prefer-rest-params
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${firstId}`);
}

function initGa(codes) {
  // Ocard 自己的永遠排第一個
  const ids = fresh('ga', [OCARD_GA].concat(codes || []));
  if (!ids.length) return;

  ensureGaBase(ids[0]);
  ids.forEach((id) => {
    loaded.ga.push(id);
    // gtag('config') 對 G-（GA4）、UA-（舊版）、AW-（Google Ads）三種前綴都適用，
    // production 的 brand_trace 這三種都有，所以不做前綴判斷、直接交給 gtag
    // send_page_view 保持開啟：config 執行時會自動送出第一次 page_view，
    // 補足 router.afterEach 在首次載入時（gtag 還沒注入）漏掉的那一次
    window.gtag('config', id, { send_page_view: true });
  });
}

// ----------------------------------------------------------------------- GTM

function initGtm(codes) {
  fresh('gtm', codes).forEach((id) => {
    loaded.gtm.push(id);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    injectScript(`https://www.googletagmanager.com/gtm.js?id=${id}`);
  });
}

/**
 * 把事件推進品牌 GTM 的 dataLayer。
 * GA4 參數直接攤在第一層（品牌用「資料層變數」就讀得到）；有 items 時，電商欄位另外放一份到 ecommerce。
 * 推電商事件前先清掉上一筆 ecommerce，這是 Google 的建議，避免 GTM 把前後兩次的內容合併。
 */
function pushToGtm(name, params) {
  if (!loaded.gtm.length) return;

  const payload = { event: name };
  const ecommerce = {};
  Object.keys(params).forEach((key) => {
    if (key !== 'items') payload[key] = params[key];
    if (ECOMMERCE_KEYS.indexOf(key) !== -1) ecommerce[key] = params[key];
  });

  if (params.items) {
    window.dataLayer.push({ ecommerce: null });
    payload.ecommerce = ecommerce;
  }
  window.dataLayer.push(payload);
}

// ---------------------------------------------------------------- Meta Pixel

function ensurePixelBase() {
  if (base.pixel) return;
  base.pixel = true;

  /* eslint-disable */
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
}

function initPixel(codes) {
  const ids = fresh('pixel', codes);
  if (!ids.length) return;

  ensurePixelBase();
  ids.forEach((id) => {
    loaded.pixel.push(id);
    window.fbq('init', id);
  });
  window.fbq('track', 'PageView');
}

// ------------------------------------------------------------------ LINE Tag

function ensureLineBase() {
  if (base.line) return;
  base.line = true;

  /* eslint-disable */
  (function(g,d,o){
    g._ltq=g._ltq||[];g._lt=g._lt||function(){g._ltq.push(arguments)};
    var h=location.protocol==='https:'?'https://d.line-scdn.net':'http://d.line-cdn.net';
    var s=d.createElement('script');s.async=1;
    s.src=o||h+'/n/line_tag/public/release/v1/lt.js';
    var t=d.getElementsByTagName('script')[0];t.parentNode.insertBefore(s,t);
  })(window, document);
  /* eslint-enable */
}

function initLine(codes) {
  const ids = fresh('line', codes);
  if (!ids.length) return;

  ensureLineBase();
  ids.forEach((id) => {
    loaded.line.push(id);
    // LINE Tag 的全域物件名稱固定是 _lt
    /* eslint-disable no-underscore-dangle */
    window._lt('init', { customerType: 'lap', tagId: id });
    window._lt('send', 'pv', [id]);
    /* eslint-enable no-underscore-dangle */
  });
}

// ------------------------------------------------------------------ 對外 API

/**
 * 注入追蹤碼。可重複呼叫（每次 API 回應後都可以叫），內部會自行去重。
 * @param {Object} tracking booknow_api 回應的 tracking 節點
 */
export function initTracking(tracking) {
  const t = tracking || {};

  initGa(t.ga);
  initGtm(t.gtm);
  initPixel(t.pixel);
  initLine(t.line);
}

/**
 * 送出 page_view。history 模式的 SPA 不會自動送，要在路由切換時手動呼叫。
 *
 * 首次載入時 API 還沒回來、追蹤碼尚未注入，這裡會直接 no-op；
 * 第一次的 page_view 由 gtag('config') 與 fbq('track', 'PageView') 自動送出，
 * 之後的路由切換才由這個函式補上，所以不會重複計算。
 *
 * @param {String} path 目前的路由路徑
 */
export function trackPageView(path) {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  if (base.pixel && window.fbq) {
    window.fbq('track', 'PageView');
  }
}

/**
 * 派送事件到 GA4、Meta Pixel，以及品牌的 GTM。
 * @param {Object} event   EVENTS 裡的一項
 * @param {Object} options ga：GA4 參數（GTM 也用這份）／pixel：Pixel 參數／eventId：去重用的事件 ID
 */
export function trackEvent(event, options) {
  const { ga = {}, pixel = {}, eventId = null } = options || {};

  if (event.ga && window.gtag) {
    window.gtag('event', event.ga, ga);
  }

  if (event.gtm) {
    pushToGtm(event.gtm, ga);
  }

  if (event.pixel && window.fbq && base.pixel) {
    window.fbq('track', event.pixel, pixel, eventId ? { eventID: eventId } : {});
  }
}

/**
 * 送出 LINE Tag 轉換事件（預約成功時用）。
 */
/* eslint-disable no-underscore-dangle */
export function trackLineConversion() {
  if (!base.line || !window._lt) return;
  loaded.line.forEach((id) => {
    window._lt('send', 'cv', { type: 'Conversion' }, [id]);
  });
}
/* eslint-enable no-underscore-dangle */

export default {
  initTracking,
  trackPageView,
  trackEvent,
  trackLineConversion,
  itemId,
  EVENTS,
  ITEM_TYPE,
};
